import { useState, useEffect } from 'react';
import {
  Activity,
  Search,
  Filter,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Clock,
  RefreshCw,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import './Events.css';

interface ResolvrEvent {
  id: string;
  source: string;
  type: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  body?: string;
  metadata?: Record<string, unknown>;
  channel: string;
  threadId?: string;
  requiresAction: boolean;
  status: 'open' | 'acknowledged' | 'resolved' | 'closed';
  createdAt: string;
  updatedAt: string;
}

interface EventStats {
  total: number;
  bySeverity: {
    critical: number;
    error: number;
    warning: number;
    info: number;
  };
  byStatus: {
    open: number;
    acknowledged: number;
    resolved: number;
  };
  actionRequired: number;
  bySource: Record<string, number>;
}

const defaultStats: EventStats = {
  total: 0,
  bySeverity: { critical: 0, error: 0, warning: 0, info: 0 },
  byStatus: { open: 0, acknowledged: 0, resolved: 0 },
  actionRequired: 0,
  bySource: {},
};

export default function Events() {
  const [events, setEvents] = useState<ResolvrEvent[]>([]);
  const [stats, setStats] = useState<EventStats>(defaultStats);
  const [sources, setSources] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<ResolvrEvent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [eventsRes, statsRes, sourcesRes] = await Promise.all([
        fetch('/api/events?limit=100'),
        fetch('/api/events/stats'),
        fetch('/api/events/sources'),
      ]);

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      if (sourcesRes.ok) {
        const data = await sourcesRes.json();
        setSources(data.sources || []);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setError('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const updateEventStatus = async (eventId: string, status: string) => {
    try {
      const res = await fetch(`/api/events/${eventId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        fetchData();
        if (selectedEvent?.id === eventId) {
          setSelectedEvent({ ...selectedEvent, status: status as any });
        }
      }
    } catch (err) {
      console.error('Failed to update event:', err);
    }
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      searchQuery === '' ||
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.source.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSeverity = severityFilter === 'all' || event.severity === severityFilter;
    const matchesStatus = statusFilter === 'all' || event.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || event.source === sourceFilter;

    return matchesSearch && matchesSeverity && matchesStatus && matchesSource;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h ago`;
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle size={16} className="severity-icon critical" />;
      case 'error':
        return <AlertCircle size={16} className="severity-icon error" />;
      case 'warning':
        return <AlertCircle size={16} className="severity-icon warning" />;
      case 'info':
        return <Info size={16} className="severity-icon info" />;
      default:
        return null;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle size={14} className="status-icon open" />;
      case 'acknowledged':
        return <Clock size={14} className="status-icon acknowledged" />;
      case 'resolved':
        return <CheckCircle size={14} className="status-icon resolved" />;
      case 'closed':
        return <CheckCircle size={14} className="status-icon closed" />;
      default:
        return null;
    }
  };

  const getSourceIcon = (source: string) => {
    const iconMap: Record<string, string> = {
      email: '📧',
      vercel: '▲',
      railway: '🚂',
      neon: '🐘',
      supabase: '⚡',
      uptime: '📊',
      github: '🐙',
    };
    return iconMap[source.toLowerCase()] || '📋';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Activity Stream</h1>
        </div>
        <div className="loading-state">Loading events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Activity Stream</h1>
        </div>
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="page-container events-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Activity Stream</h1>
          <p>All webhook events across services</p>
        </div>
        <button className="btn btn-secondary" onClick={fetchData}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="events-stats">
        <div className="event-stat">
          <div className="event-stat-icon">
            <Activity size={20} />
          </div>
          <div className="event-stat-content">
            <div className="event-stat-value">{stats.total}</div>
            <div className="event-stat-label">Total Events</div>
          </div>
        </div>
        <div className="event-stat critical">
          <div className="event-stat-icon">
            <AlertTriangle size={20} />
          </div>
          <div className="event-stat-content">
            <div className="event-stat-value">{stats.bySeverity.critical}</div>
            <div className="event-stat-label">Critical</div>
          </div>
        </div>
        <div className="event-stat error">
          <div className="event-stat-icon">
            <AlertCircle size={20} />
          </div>
          <div className="event-stat-content">
            <div className="event-stat-value">{stats.bySeverity.error}</div>
            <div className="event-stat-label">Errors</div>
          </div>
        </div>
        <div className="event-stat action">
          <div className="event-stat-icon">
            <Clock size={20} />
          </div>
          <div className="event-stat-content">
            <div className="event-stat-value">{stats.actionRequired}</div>
            <div className="event-stat-label">Action Required</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="events-toolbar">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Severity</option>
            <option value="critical">Critical</option>
            <option value="error">Error</option>
            <option value="warning">Warning</option>
            <option value="info">Info</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Sources</option>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Events List */}
      <div className="events-content">
        <div className="events-list">
          {filteredEvents.length === 0 ? (
            <div className="empty-state">
              <Activity size={48} />
              <h3>No events found</h3>
              <p>Try adjusting your search or filters</p>
            </div>
          ) : (
            filteredEvents.map((event) => (
              <div
                key={event.id}
                className={`event-item ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                onClick={() => setSelectedEvent(event)}
              >
                <div className="event-icon">
                  <span className="source-icon">{getSourceIcon(event.source)}</span>
                </div>
                <div className="event-content">
                  <div className="event-header">
                    <span className="event-source">{event.source}</span>
                    <span className="event-type">{event.type}</span>
                    <span className="event-time">{formatDate(event.createdAt)}</span>
                  </div>
                  <div className="event-title">{event.title}</div>
                  <div className="event-footer">
                    <div className="event-badges">
                      {getSeverityIcon(event.severity)}
                      <span className={`severity-badge ${event.severity}`}>{event.severity}</span>
                      {getStatusIcon(event.status)}
                      <span className={`status-badge ${event.status}`}>{event.status}</span>
                      {event.requiresAction && (
                        <span className="action-badge">Action Required</span>
                      )}
                    </div>
                    <span className="event-channel">#{event.channel}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="event-chevron" />
              </div>
            ))
          )}
        </div>

        {/* Event Detail Sidebar */}
        {selectedEvent && (
          <div className="event-detail-panel">
            <div className="event-detail-header">
              <div className="event-detail-source">
                <span className="source-icon large">{getSourceIcon(selectedEvent.source)}</span>
                <div>
                  <div className="source-name">{selectedEvent.source}</div>
                  <div className="source-type">{selectedEvent.type}</div>
                </div>
              </div>
              <button className="close-btn" onClick={() => setSelectedEvent(null)}>×</button>
            </div>

            <div className="event-detail-body">
              <h2>{selectedEvent.title}</h2>

              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">Severity</span>
                  <span className={`severity-badge ${selectedEvent.severity}`}>
                    {getSeverityIcon(selectedEvent.severity)}
                    {selectedEvent.severity}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${selectedEvent.status}`}>
                    {selectedEvent.status}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Channel</span>
                  <span className="detail-value">#{selectedEvent.channel}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created</span>
                  <span className="detail-value">{new Date(selectedEvent.createdAt).toLocaleString()}</span>
                </div>
                {selectedEvent.threadId && (
                  <div className="detail-row">
                    <span className="detail-label">Discord Thread</span>
                    <span className="detail-value">{selectedEvent.threadId}</span>
                  </div>
                )}
              </div>

              {selectedEvent.body && (
                <div className="detail-section">
                  <h3>Details</h3>
                  <pre className="event-body">{selectedEvent.body}</pre>
                </div>
              )}

              {selectedEvent.metadata && Object.keys(selectedEvent.metadata).length > 0 && (
                <div className="detail-section">
                  <h3>Metadata</h3>
                  <pre className="event-metadata">
                    {JSON.stringify(selectedEvent.metadata, null, 2)}
                  </pre>
                </div>
              )}

              <div className="event-actions">
                {selectedEvent.status === 'open' && (
                  <>
                    <button
                      className="btn btn-secondary"
                      onClick={() => updateEventStatus(selectedEvent.id, 'acknowledged')}
                    >
                      Acknowledge
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => updateEventStatus(selectedEvent.id, 'resolved')}
                    >
                      <CheckCircle size={14} />
                      Resolve
                    </button>
                  </>
                )}
                {selectedEvent.status === 'acknowledged' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => updateEventStatus(selectedEvent.id, 'resolved')}
                  >
                    <CheckCircle size={14} />
                    Resolve
                  </button>
                )}
                {(selectedEvent.status === 'resolved' || selectedEvent.status === 'closed') && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => updateEventStatus(selectedEvent.id, 'open')}
                  >
                    Reopen
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
